import type File from '../../interfaces/File';
import type { FileTypeEnum } from '../../enums/FileTypeEnum';

export type ViewerDrawerKind = 'viewer' | 'office';

/** Default drawer height (`95vh`). Max allows full cover (`100vh`). */
export const VIEWER_DRAWER_DEFAULT_SIZE_VH = 95;
export const VIEWER_DRAWER_MIN_SIZE_VH = 40;
export const VIEWER_DRAWER_MAX_SIZE_VH = 100;
/** Approximate Drawer header chrome for body `calc` height. */
export const VIEWER_DRAWER_HEADER_OFFSET_PX = 55;
/** Ant Design Drawer default z-index. */
export const VIEWER_DRAWER_Z_INDEX = 1000;
/** @deprecated Nested drawer panel replaced stacking; kept for any stale imports. */
export const RIGHT_PANEL_ABOVE_VIEWER_DRAWER_Z_INDEX = 1100;
/** Nested Details/AI split width inside FilegrantViewerDrawer (matches global RightPanel). */
export const RIGHT_PANEL_WIDTH_PX = 480;
/** @deprecated No longer used — drawer stays full width with nested panel. */
export const VIEWER_DRAWER_MIN_WIDTH_PX = 320;

export function clampViewerDrawerSizeVh(vh: number): number {
  if (!Number.isFinite(vh)) return VIEWER_DRAWER_DEFAULT_SIZE_VH;
  return Math.min(VIEWER_DRAWER_MAX_SIZE_VH, Math.max(VIEWER_DRAWER_MIN_SIZE_VH, Math.round(vh)));
}

export type ViewerDrawerOpenParams = {
  fileId: number | string;
  /** Snapshot for RightPanel Details/Chat when list selection empty (filename open). */
  file?: File | null;
  version?: string | null;
  publicFileToken?: string | null;
  acToken?: string | null;
  isReadOnly?: boolean;
};

export type OfficeDrawerOpenParams = {
  fileId: number | string;
  fileType: FileTypeEnum;
  /** Snapshot for RightPanel Details/Chat when list selection empty. */
  file?: File | null;
};

export interface ViewerDrawerSlice {
  isViewerDrawerOpen: boolean;
  viewerDrawerKind: ViewerDrawerKind | null;
  viewerDrawerFileId: string | null;
  viewerDrawerFileType: FileTypeEnum | null;
  viewerDrawerVersion: string | null;
  viewerDrawerPublicFileToken: string | null;
  viewerDrawerAcToken: string | null;
  viewerDrawerIsReadOnly: boolean;
  /** File meta for RightPanel sync while drawer open (selection may be empty). */
  viewerDrawerFile: File | null;
  /** User-managed bottom-drawer height in vh; persisted separately from open session. */
  viewerDrawerSizeVh: number;
  openViewerDrawer: (params: ViewerDrawerOpenParams) => void;
  openOfficeDrawer: (params: OfficeDrawerOpenParams) => void;
  closeViewerDrawer: () => void;
  setViewerDrawerFile: (file: File | null) => void;
  setViewerDrawerSizeVh: (vh: number) => void;
}

export const createViewerDrawerSlice = (set: any): ViewerDrawerSlice => ({
  isViewerDrawerOpen: false,
  viewerDrawerKind: null,
  viewerDrawerFileId: null,
  viewerDrawerFileType: null,
  viewerDrawerVersion: null,
  viewerDrawerPublicFileToken: null,
  viewerDrawerAcToken: null,
  viewerDrawerIsReadOnly: false,
  viewerDrawerFile: null,
  viewerDrawerSizeVh: VIEWER_DRAWER_DEFAULT_SIZE_VH,
  openViewerDrawer: (params) =>
    set({
      isViewerDrawerOpen: true,
      viewerDrawerKind: 'viewer',
      viewerDrawerFileId: String(params.fileId),
      viewerDrawerFileType: null,
      viewerDrawerVersion: params.version ?? null,
      viewerDrawerPublicFileToken: params.publicFileToken ?? null,
      viewerDrawerAcToken: params.acToken ?? null,
      viewerDrawerIsReadOnly: params.isReadOnly ?? false,
      viewerDrawerFile: params.file ?? null,
    }),
  openOfficeDrawer: (params) =>
    set({
      isViewerDrawerOpen: true,
      viewerDrawerKind: 'office',
      viewerDrawerFileId: String(params.fileId),
      viewerDrawerFileType: params.fileType,
      viewerDrawerVersion: null,
      viewerDrawerPublicFileToken: null,
      viewerDrawerAcToken: null,
      viewerDrawerIsReadOnly: false,
      viewerDrawerFile: params.file ?? null,
    }),
  closeViewerDrawer: () =>
    set({
      isViewerDrawerOpen: false,
      viewerDrawerKind: null,
      viewerDrawerFileId: null,
      viewerDrawerFileType: null,
      viewerDrawerVersion: null,
      viewerDrawerPublicFileToken: null,
      viewerDrawerAcToken: null,
      viewerDrawerIsReadOnly: false,
      viewerDrawerFile: null,
    }),
  setViewerDrawerFile: (file) => set({ viewerDrawerFile: file }),
  setViewerDrawerSizeVh: (vh) => set({ viewerDrawerSizeVh: clampViewerDrawerSizeVh(vh) }),
});
